// Historical borders: one vector tileset with every era (pipeline: borders.py); the layers show only the features
// valid at the playhead (t0 <= t < t1), refiltered when the playhead crosses one of the dates at which borders change.

import type maplibregl from "maplibre-gl";

export const BORDER_LAYERS = [
  "hist-fill", "hist-fill-occ", "hist-sub-occ", "hist-line", "hist-line-approx", "hist-sub", "hist-sub-occ-line",
] as const;
export const BORDER_LABEL_LAYER = "hist-label";
const PLACE_LAYER = "place-label"; // basemap places carry t0/t1 too (pipeline: basemap.place_names)

// Muted, distinct hues for neighbouring polities (the pipeline colours neighbours differently: c = 0…11).
const PALETTE = [8, 32, 52, 88, 128, 160, 186, 206, 228, 258, 290, 322].map((h) => `hsl(${h}, 38%, 56%)`);
const LINE = "#cbb88d";
const OCC = "#d98a7e";
const SRC = "hist";
type Filter = maplibregl.FilterSpecification;

const inTime = (t: number): unknown[] => ["all", ["<=", ["get", "t0"], t], [">", ["get", "t1"], t]];
const withTime = (t: number, extra: unknown[] | null): Filter =>
  (extra ? ["all", inTime(t), extra] : inTime(t)) as Filter;

// Per-layer extra filters (combined with the time filter).
const EXTRA: Record<string, unknown[] | null> = {
  "hist-fill": ["!=", ["get", "k"], 2],
  "hist-fill-occ": ["==", ["get", "k"], 2],
  "hist-sub-occ": ["==", ["get", "k"], 2],
  "hist-line": ["!=", ["get", "ap"], 1],
  "hist-line-approx": ["==", ["get", "ap"], 1],
  "hist-sub": ["!=", ["get", "k"], 2],
  "hist-sub-occ-line": ["==", ["get", "k"], 2],
  "hist-label": null,
};

/** Diagonal hatching for wartime administrations (occupation regimes). */
function hatch(): ImageData {
  const n = 12;
  const c = document.createElement("canvas");
  c.width = c.height = n;
  const g = c.getContext("2d")!;
  g.strokeStyle = "rgba(217, 138, 126, 0.55)";
  g.lineWidth = 1.3;
  g.beginPath();
  for (let i = -n; i <= 2 * n; i += n / 2) {
    g.moveTo(i, n);
    g.lineTo(i + n, 0);
  }
  g.stroke();
  return g.getImageData(0, 0, n, n);
}

/** Index of the border epoch containing t: the number of change dates <= t. */
export function epochIndex(changes: number[], t: number): number {
  let lo = 0, hi = changes.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (changes[mid] <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The moment that stands for t's whole epoch: just after the last change at or before it. Features begin and end
 * only at change dates (place names within rounding, 0.00005 years), so the filter shows the same features as t
 * itself, and stays identical through the epoch: MapLibre then skips re-reading the tiles. */
export function epochTime(changes: number[], t: number): number {
  const k = epochIndex(changes, t);
  return k > 0 ? changes[k - 1] + 0.00006 : changes.length ? changes[0] - 1 : t;
}

/** Add the borders source and layers: fills and lines before `beforeFill` (the ocean mask), labels before `beforeLabel`. */
export function addBorderLayers(map: maplibregl.Map, url: string, beforeFill: string, beforeLabel: string, t: number) {
  if (!map.hasImage("hatch")) map.addImage("hatch", hatch(), { pixelRatio: 2 });
  map.addSource(SRC, { type: "vector", url, attribution: '<a href="https://www.openhistoricalmap.org/" target="_blank" rel="noopener">OpenHistoricalMap</a> (CC0)' });
  const width = (a: number, b: number): maplibregl.ExpressionSpecification =>
    ["interpolate", ["linear"], ["zoom"], 1, a, 6, b, 10, b * 1.6];
  const add = (layer: maplibregl.LayerSpecification, before: string) =>
    map.addLayer({ ...layer, filter: withTime(t, EXTRA[layer.id] ?? null) } as maplibregl.LayerSpecification, before);
  add({ id: "hist-fill", type: "fill", source: SRC, "source-layer": "polity",
    paint: { "fill-color": ["match", ["get", "c"], ...PALETTE.flatMap((col, i) => [i, col]), PALETTE[0]] as never, "fill-opacity": 0.13 } }, beforeFill);
  add({ id: "hist-fill-occ", type: "fill", source: SRC, "source-layer": "polity", paint: { "fill-pattern": "hatch", "fill-opacity": 0.8 } }, beforeFill);
  add({ id: "hist-sub-occ", type: "fill", source: SRC, "source-layer": "sub", paint: { "fill-pattern": "hatch", "fill-opacity": 0.7 } }, beforeFill);
  add({ id: "hist-sub", type: "line", source: SRC, "source-layer": "sub", minzoom: 3,
    paint: { "line-color": LINE, "line-opacity": 0.22, "line-width": width(0.3, 0.7), "line-dasharray": [3, 2] } }, beforeFill);
  add({ id: "hist-line", type: "line", source: SRC, "source-layer": "polity", layout: { "line-join": "round" },
    paint: { "line-color": LINE, "line-opacity": 0.42, "line-width": width(0.45, 1.1) } }, beforeFill);
  add({ id: "hist-line-approx", type: "line", source: SRC, "source-layer": "polity", layout: { "line-join": "round" },
    paint: { "line-color": LINE, "line-opacity": 0.45, "line-width": width(0.45, 1.1), "line-dasharray": [2, 1.6] } }, beforeFill);
  add({ id: "hist-sub-occ-line", type: "line", source: SRC, "source-layer": "sub",
    paint: { "line-color": OCC, "line-opacity": 0.55, "line-width": width(0.4, 0.9), "line-dasharray": [2.5, 1.5] } }, beforeFill);
  add({
    id: BORDER_LABEL_LAYER, type: "symbol", source: SRC, "source-layer": "label",
    layout: {
      "text-field": ["get", "n"],
      "text-font": ["Noto Sans Italic"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 1, ["case", [">", ["get", "l"], 2], 8.5, 9.5], 6,
        ["case", [">", ["get", "l"], 2], 11, 13.5]] as never,
      "text-max-width": 8, "text-letter-spacing": 0.04, "text-padding": 6,
      "symbol-sort-key": ["-", 0, ["get", "r"]] as never,
    },
    paint: {
      "text-color": ["case", ["==", ["get", "k"], 2], OCC, [">", ["get", "l"], 2], "#a99a78", LINE] as never,
      "text-opacity": ["case", [">", ["get", "l"], 2], 0.6, 0.75] as never,
      "text-halo-color": "#0b1016", "text-halo-width": 1.2,
    },
  }, beforeLabel);
}

/** Show the borders valid at tBorders and the place names valid at tPlaces (each an epochTime: a filter that did not
 * change is not applied again, so a border change does not make the basemap, where the names are, reload). */
export function setBorderTime(map: maplibregl.Map, tBorders: number, tPlaces: number) {
  for (const id of [...BORDER_LAYERS, BORDER_LABEL_LAYER]) if (map.getLayer(id)) map.setFilter(id, withTime(tBorders, EXTRA[id] ?? null));
  if (map.getLayer(PLACE_LAYER)) map.setFilter(PLACE_LAYER, withTime(tPlaces, null)); // historical names (Königsberg, Stalingrad, …)
}

export function setBordersVisible(map: maplibregl.Map, on: boolean) {
  for (const id of [...BORDER_LAYERS, BORDER_LABEL_LAYER]) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}
